import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import {
  AssessmentPageStepper,
  type AssessmentPageStepperNode,
} from "../../../components/AssessmentPageStepper";
import { Layout } from "../../../components/Layout";
import { AssessmentLoadingState } from "../../../components/AssessmentLoadingState";
import { useAssessmentSession } from "../../../contexts/assessmentUserContext";
import { useAssessmentContent } from "../../../contexts/assessmentContentContext";
import type { ListeningAssessmentContent } from "./steps";
import { listeningSteps } from "./ListeningAssessmentSteps";
import { findRawQuestionMetadata } from "../assessmentNormalization";
import { normalizeListeningAssessmentContent } from "./utils";
import { useSubmissionIdFromParams } from "../hooks/useSubmissionIdFromParams";
import {
  resolveFetchableStep,
  resolveResumeStepIndex,
  useAssessmentStepFlow,
} from "../hooks/useAssessmentStepFlow";
import { FinishAssessmentModal } from "../../../components/FinishAssessmentModal";
import { AssessmentErrorModal } from "../../../components/AssessmentErrorModal";
import { exitAssessment } from "../../../lib/assessmentExit";
import { useAssessmentEventer } from "../../../lib/eventer/useAssessmentEventer";
import { useAnswerSaveTracker } from "../hooks/useAnswerSaveTracker";

const RETRIES_BEFORE_SAVE_AND_QUIT = 2;

function isIgnoredListeningStepperStep(stepKey: string): boolean {
  return stepKey === "instructions" || stepKey.includes("instructions");
}

type ListeningAssessmentProps = {
  isDemo?: boolean;
};

export function ListeningAssessment({
  isDemo = false,
}: ListeningAssessmentProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const submissionId = useSubmissionIdFromParams();
  const demoGrade = searchParams.get("grade")?.trim() || "12";
  const activeSubmissionId = isDemo ? "" : submissionId;
  const {
    actualRole,
    isLoading: isSessionLoading,
    me,
  } = useAssessmentSession();
  const {
    fetchAssessmentContent,
    saveSubmissionAnswer,
    saveSubmissionAudio,
    saveSubmissionProgress,
  } = useAssessmentContent();
  const trackEvent = useAssessmentEventer();
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [assessment, setAssessment] =
    useState<ListeningAssessmentContent | null>(null);
  const [rawAssessment, setRawAssessment] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasLoadFailed, setHasLoadFailed] = useState<boolean>(false);
  const [loadAttempt, setLoadAttempt] = useState<number>(0);
  const [loadRetryCount, setLoadRetryCount] = useState<number>(0);
  const [hasStepSaveFailed, setHasStepSaveFailed] = useState<boolean>(false);
  const [saveRetryCount, setSaveRetryCount] = useState<number>(0);
  const [isRetryingSave, setIsRetryingSave] = useState<boolean>(false);
  const [openFinishModal, setOpenFinishModal] = useState<boolean>(false);
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);
  const pendingStepActionRef = useRef<(() => void) | null>(null);
  const answerSaves = useAnswerSaveTracker();
  const userId = me?.id;

  const currentStep = listeningSteps[stepIndex] ?? listeningSteps[0];
  if (!currentStep) {
    throw new Error("No listening steps configured.");
  }
  const fetchStep = resolveFetchableStep(listeningSteps, currentStep);
  const location = `listening/${currentStep.key}`;

  const CurrentStepComponent = currentStep.component;
  const isLastStep = stepIndex >= listeningSteps.length - 1;
  const { canFinish, isCurrentStepReady, handleStepReadyChange } =
    useAssessmentStepFlow(currentStep, isLastStep);
  const visibleStepperSteps = listeningSteps.filter(
    (step) => !isIgnoredListeningStepperStep(step.key),
  );
  function getStepperLabel(stepKey: string): string {
    switch (stepKey) {
      case "part1-prompt":
        return "1";
      case "part2-prompt":
        return "2";
      case "part3-prompt":
        return "3";
      case "listening-recap":
        return "4";
      default:
        return "";
    }
  }
  const stepperNodes: AssessmentPageStepperNode[] = visibleStepperSteps.map(
    (step) => ({
      id: step.key,
      isMajor: [
        "part1-prompt",
        "part2-prompt",
        "part3-prompt",
        "listening-recap",
      ].includes(step.key),
      label: getStepperLabel(step.key),
    }),
  );
  const activeStepperIndex = (() => {
    const exactIndex = stepperNodes.findIndex(
      (node) => node.id === currentStep.key,
    );
    if (exactIndex >= 0) {
      return exactIndex;
    }

    for (let index = stepIndex - 1; index >= 0; index -= 1) {
      const candidateStep = listeningSteps[index];
      if (!candidateStep || isIgnoredListeningStepperStep(candidateStep.key)) {
        continue;
      }
      const visibleIndex = stepperNodes.findIndex(
        (node) => node.id === candidateStep.key,
      );
      if (visibleIndex >= 0) {
        return visibleIndex;
      }
    }

    return 0;
  })();

  useEffect(() => {
    if (!isDemo || isSessionLoading || actualRole !== "student") {
      return;
    }

    navigate("/listening", { replace: true });
  }, [actualRole, isDemo, isSessionLoading, navigate]);

  useEffect(() => {
    trackEvent({
      action: "assessment_step_viewed",
      data: {
        testType: "listening",
        stepKey: currentStep.key,
        submissionId: activeSubmissionId || null,
        isDemo,
      },
    }).then();
  }, [activeSubmissionId, currentStep.key, isDemo, trackEvent]);

  useEffect(() => {
    const controller = new AbortController();

    const markLoadFailed = (loadError: unknown) => {
      console.error(
        `Listening assessment step load failed for ${fetchStep.key}`,
        loadError,
      );
      setAssessment(null);
      setRawAssessment(null);
      setHasLoadFailed(true);
    };

    const loadAssessmentStep = async () => {
      if (isDemo && actualRole === "student") {
        return;
      }
      if (fetchStep.shouldFetch === false) {
        setHasLoadFailed(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setHasLoadFailed(false);

        const content = await fetchAssessmentContent({
          testType: "listening",
          grade: isDemo ? demoGrade : undefined,
          isDemo,
          submissionId: activeSubmissionId,
          userId,
          includePart: fetchStep.includePart,
          part: fetchStep.partOrder ?? 1,
          location: `listening/${fetchStep.key}`,
          signal: controller.signal,
        });

        if (!content) {
          markLoadFailed(new Error("Assessment content is unavailable."));
          return;
        }
        setRawAssessment(content);
        const resolvedAssessment = normalizeListeningAssessmentContent(content);
        setAssessment(resolvedAssessment);
        setLoadRetryCount(0);
        const completedStepKeys =
          resolvedAssessment.submissionProgress?.completed_step_keys ?? [];
        if (completedStepKeys.length === 0) {
          return;
        }
        const newStep = resolveResumeStepIndex(
          listeningSteps,
          completedStepKeys,
        );
        setStepIndex(newStep);
      } catch (requestError: unknown) {
        if (
          requestError instanceof DOMException &&
          requestError.name === "AbortError"
        ) {
          return;
        }
        markLoadFailed(requestError);
      } finally {
        setIsLoading(false);
      }
    };

    loadAssessmentStep().then();

    return () => {
      controller.abort();
    };
  }, [
    fetchStep.includePart,
    fetchStep.key,
    fetchStep.partOrder,
    fetchStep.requestStep,
    fetchStep.shouldFetch,
    fetchAssessmentContent,
    activeSubmissionId,
    actualRole,
    demoGrade,
    isDemo,
    userId,
    loadAttempt,
  ]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    setIsNextDisabled(false);
  }, [currentStep.key, isLoading]);

  const completeCurrentStep = async (
    nextAction: () => void,
  ): Promise<boolean> => {
    pendingStepActionRef.current = nextAction;

    const answersSaved = await answerSaves.flushSaves();
    if (!answersSaved) {
      return false;
    }

    if (activeSubmissionId) {
      try {
        await saveSubmissionProgress({
          submissionId: activeSubmissionId,
          stepKey: currentStep.key,
          location,
        });
      } catch (saveError: unknown) {
        console.error("Listening assessment save progress failed", saveError);
        setHasStepSaveFailed(true);
        return false;
      }
    }

    await trackEvent({
      action: "assessment_step_completed",
      data: {
        testType: "listening",
        stepKey: currentStep.key,
        submissionId: activeSubmissionId || null,
        isDemo,
      },
    });

    pendingStepActionRef.current = null;
    setHasStepSaveFailed(false);
    nextAction();
    return true;
  };

  const saveAnswer = (questionId: string, answer: string | null) => {
    if (!activeSubmissionId) {
      return;
    }

    void trackEvent({
      action: "assessment_answer_clicked",
      data: {
        testType: "listening",
        stepKey: currentStep.key,
        submissionId: activeSubmissionId,
        questionId,
      },
    });
    const metadata = findRawQuestionMetadata(rawAssessment, questionId);
    const answerLocation = location;

    return answerSaves.trackSave(questionId, answer, async () => {
      try {
        await saveSubmissionAnswer({
          submissionId: activeSubmissionId,
          questionId,
          answer,
          metadata,
          location: answerLocation,
        });
      } catch (saveError: unknown) {
        console.error("Listening assessment save answer failed", saveError);
        throw saveError;
      }
    });
  };

  const handleRetrySave = async () => {
    setIsRetryingSave(true);
    const answersSaved = await answerSaves.retryFailedSaves();
    const pendingStepAction = pendingStepActionRef.current;
    const saved =
      answersSaved &&
      (pendingStepAction ? await completeCurrentStep(pendingStepAction) : true);
    setSaveRetryCount((count) => (saved ? 0 : count + 1));
    setIsRetryingSave(false);
  };

  const handleRetryLoad = () => {
    setLoadRetryCount((count) => count + 1);
    setLoadAttempt((attempt) => attempt + 1);
  };

  const handleSaveAndQuit = () => {
    trackEvent({
      action: "assessment_exit_clicked",
      data: {
        source: "listening",
        stepKey: currentStep.key,
        submissionId: activeSubmissionId || null,
        isDemo,
      },
    }).then();
    exitAssessment();
  };

  const hasSaveFailed = answerSaves.hasFailedSaves || hasStepSaveFailed;
  const isErrorModalOpen = hasLoadFailed || hasSaveFailed;
  const errorRetryCount = hasLoadFailed ? loadRetryCount : saveRetryCount;

  if (isLoading) {
    return (
      <Layout>
        <AssessmentLoadingState testId="listening-assessment-loading" />
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        flexDirection={"column"}
        display={"flex"}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 1400,
          mx: "auto",
          minHeight: "100dvh",
          boxSizing: "border-box",
        }}
        p={3}
      >
        <Stack direction={"column"} sx={{ alignSelf: "flex-start", mb: 3 }}>
          <Typography variant="h4">Listening</Typography>
          <AssessmentPageStepper
            nodes={stepperNodes}
            activeIndex={activeStepperIndex}
          />
        </Stack>
        <Box
          className="notranslate"
          flex={1}
          display={"flex"}
          alignItems={"center"}
          width={"100%"}
        >
          {hasLoadFailed ? null : (
            <CurrentStepComponent
              key={currentStep.key}
              assessment={assessment}
              isLoading={isLoading}
              onStepReadyChange={handleStepReadyChange}
              onSaveAnswer={saveAnswer}
              onSaveAudio={async ({
                entityType,
                partOrder,
                questionId,
                blob,
                mimeType,
                durationMs,
              }) => {
                if (!activeSubmissionId) {
                  console.error(
                    "Listening assessment save audio skipped: missing submission id.",
                  );
                  return;
                }

                try {
                  await saveSubmissionAudio({
                    submissionId: activeSubmissionId,
                    entityType,
                    partOrder,
                    questionId,
                    blob,
                    mimeType,
                    durationMs,
                    location,
                  });
                } catch (saveError: unknown) {
                  console.error(
                    "Listening assessment save audio failed",
                    saveError,
                  );
                }
              }}
              {...currentStep.componentProps}
            />
          )}
        </Box>
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: "auto", pt: 2 }}
          justifyContent={"space-between"}
        >
          <Button
            size={"large"}
            variant={"outlined"}
            onClick={handleSaveAndQuit}
          >
            Save and Quit
          </Button>
          {canFinish ? (
            <Button
              disabled={
                !isCurrentStepReady || isNextDisabled || isErrorModalOpen
              }
              onClick={() => {
                setIsNextDisabled(true);
                trackEvent({
                  action: "assessment_finish_clicked",
                  data: {
                    testType: "listening",
                    stepKey: currentStep.key,
                    submissionId: activeSubmissionId || null,
                    isDemo,
                  },
                }).then();
                void completeCurrentStep(() => setOpenFinishModal(true));
              }}
            >
              Finish
            </Button>
          ) : (
            <Button
              size={"large"}
              disabled={
                isLastStep ||
                !isCurrentStepReady ||
                isNextDisabled ||
                isErrorModalOpen
              }
              onClick={() => {
                setIsNextDisabled(true);
                const nextIndex = Math.min(
                  stepIndex + 1,
                  listeningSteps.length - 1,
                );
                const nextFetchStep = resolveFetchableStep(
                  listeningSteps,
                  listeningSteps[nextIndex]!,
                );
                const needsFetch = nextFetchStep.key !== fetchStep.key;
                void completeCurrentStep(() => {
                  if (needsFetch) {
                    setIsLoading(true);
                  }
                  setStepIndex(nextIndex);
                });
              }}
            >
              Next
            </Button>
          )}
        </Stack>
      </Box>
      <FinishAssessmentModal open={openFinishModal} />
      <AssessmentErrorModal
        open={isErrorModalOpen}
        kind={hasLoadFailed ? "load" : "save"}
        isRetrying={isRetryingSave}
        showSaveAndQuit={errorRetryCount >= RETRIES_BEFORE_SAVE_AND_QUIT}
        onRetry={hasLoadFailed ? handleRetryLoad : () => void handleRetrySave()}
        onSaveAndQuit={handleSaveAndQuit}
      />
    </Layout>
  );
}
